import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { usePageSellerId } from '@dashboard/hooks/usePage';
import {
  attachPageOrderBump,
  detachPageOrderBump,
  getPageOrderBumpInfo,
  getPageOrderBumps,
  reorderPageOrderBumps,
  updatePageOrderBump,
} from '@dashboard/hooks/queries/usePageQueries';
import { pageKey, pageOrderBumpInfoKey, pageOrderBumpsKey } from '@dashboard/lib/queryKeys';

/**
 * The page GET returns the RAW placement entries (productId, enabled, title,
 * description, quantity) — only the order-bump endpoints resolve them against
 * their products into rows the editor can render. This query is therefore the
 * list's source of truth, and every mutation response replaces it wholesale.
 */
export function useOrderBumps(pageId, { enabled = true } = {}) {
  const { token } = useAuth();
  const sellerId = usePageSellerId();

  const { data, isLoading, isError } = useQuery({
    queryKey: pageOrderBumpsKey(pageId),
    queryFn: () => getPageOrderBumps(sellerId, token, pageId),
    enabled,
  });

  return {
    orderBumps: data?.orderBumps ?? [],
    orderBumpProducts: data?.orderBumpProducts ?? [],
    isLoading,
    isError,
  };
}

// Sales are aggregated over every payment on the pages offering the bump, so
// the Info dialog asks only while it is open and reuses the answer for a while.
export function useOrderBumpInfo(pageId, productId, { enabled = true } = {}) {
  const { token } = useAuth();
  const sellerId = usePageSellerId();

  return useQuery({
    queryKey: pageOrderBumpInfoKey(pageId, productId),
    queryFn: () => getPageOrderBumpInfo(sellerId, token, pageId, productId),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export default function useOrderBumpMutations(pageId) {
  const { token } = useAuth();
  const sellerId = usePageSellerId();
  const queryClient = useQueryClient();

  // An entry whose offer stopped resolving is absent from the response even
  // though it is still attached, so the list is replaced rather than merged —
  // anything the API omits is exactly what checkout would omit too.
  const applyResponse = response => {
    if (!response?.orderBumps) return;

    queryClient.setQueryData(pageOrderBumpsKey(pageId), response);

    // billingModel widens with the enabled bumps and other tabs gate on it, so
    // patch it (and the placements) back onto the page without a refetch.
    queryClient.setQueryData(pageKey(pageId), old =>
      old ? { ...old, orderBumps: response.orderBumps, billingModel: response.billingModel } : old,
    );
  };

  const onError = error =>
    toast({ title: error?.message || 'Unable to update order bumps', type: 'error' });

  const attach = useMutation({
    // A product id, or `{ productId, placement }` to attach with settings.
    mutationFn: variables =>
      typeof variables === 'object'
        ? attachPageOrderBump(sellerId, token, pageId, variables.productId, variables.placement)
        : attachPageOrderBump(sellerId, token, pageId, variables),
    onSuccess: applyResponse,
    onError,
  });

  const update = useMutation({
    mutationFn: ({ productId, ...patch }) =>
      updatePageOrderBump(sellerId, token, pageId, productId, patch),
    onSuccess: applyResponse,
    onError,
  });

  const detach = useMutation({
    mutationFn: productId => detachPageOrderBump(sellerId, token, pageId, productId),
    onSuccess: applyResponse,
    onError,
  });

  /**
   * Reorder writes the new order to the cache on drop rather than on response.
   *
   * The dragged row renders from the cache the instant the drag ends, so without
   * this the list snapped back to the old order for the length of the round trip
   * and then forward again — a visible flash either side of the drop.
   */
  const reorder = useMutation({
    mutationFn: productIds => reorderPageOrderBumps(sellerId, token, pageId, productIds),
    onMutate: async productIds => {
      await queryClient.cancelQueries({ queryKey: pageOrderBumpsKey(pageId) });

      const previous = queryClient.getQueryData(pageOrderBumpsKey(pageId));
      const previousPageOrderBumps = queryClient.getQueryData(pageKey(pageId))?.orderBumps;

      const sortByPayload = entries => {
        const byId = new Map((entries ?? []).map(entry => [String(entry.productId), entry]));
        return productIds.map(id => byId.get(String(id))).filter(Boolean);
      };

      queryClient.setQueryData(pageOrderBumpsKey(pageId), old =>
        old?.orderBumps ? { ...old, orderBumps: sortByPayload(old.orderBumps) } : old,
      );

      // The preview reads the page copy, so it has to move with the list.
      queryClient.setQueryData(pageKey(pageId), old =>
        old?.orderBumps ? { ...old, orderBumps: sortByPayload(old.orderBumps) } : old,
      );

      return { previous, previousPageOrderBumps };
    },
    onSuccess: applyResponse,
    onError: (error, _productIds, context) => {
      if (context?.previous) {
        queryClient.setQueryData(pageOrderBumpsKey(pageId), context.previous);
      }
      // The preview moved with the list on drop, so it has to move back too.
      if (context?.previousPageOrderBumps) {
        queryClient.setQueryData(pageKey(pageId), old =>
          old ? { ...old, orderBumps: context.previousPageOrderBumps } : old,
        );
      }
      onError(error);
    },
  });

  return { attach, update, detach, reorder };
}
